import { Link } from 'react-router-dom';
import { Lock, Sparkles } from 'lucide-react';
import { useEntitlements } from '../contexts/EntitlementsContext';
import { useLAScope } from '../contexts/LAScopeContext';
import { Button } from './ui/button';

/** Tiny banner shown above any list of gated CTAs. */
export function PlanBadge({ className = '' }) {
  const { ent, planLabel } = useEntitlements();
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-[4px] bg-ink/5 border border-ink/10 ${className}`}>
      <Sparkles className="w-3 h-3 text-brand-orange" />
      <span className="font-body text-[10px] uppercase tracking-wider text-ink/60">Your plan:</span>
      <span className="font-display text-[11px] font-extrabold text-ink">{planLabel(ent.plan_id)}</span>
    </span>
  );
}

/**
 * Wraps any element. If user has `feature`, renders children unchanged.
 * If not, renders an upgrade card with a CTA pointing to /pricing.
 *
 * Props:
 *   feature   – feature key (e.g. 'csv_export', 'council_level_report')
 *   minPlan   – optional explicit plan name to show in the prompt
 *   inline    – when true, renders a small inline lock icon instead of a card
 *   children  – the gated content
 */
export default function Gated({ feature, minPlan, inline = false, title, children, testId }) {
  const { has, planLabel, loading, ent } = useEntitlements();
  const { laScope, setLAScope, scopes } = useLAScope();
  if (loading) return children;
  if (has(feature)) return children;

  const licence = ent?.district_licence;
  const licenceName = scopes.find(s => s.slug === licence)?.short;
  const scopeName = scopes.find(s => s.slug === laScope)?.label || 'All Tees Valley';

  const reqPlan = minPlan || (
    feature === 'csv_export' || feature === 'council_level_report' || feature === 'sensitivity_analysis'
      ? 'premium'
      : feature === 'api_access' || feature === 'multi_authority' || feature === 'white_label_export'
        ? 'enterprise'
        : 'pro'
  );

  if (inline) {
    return (
      <Link
        to="/pricing"
        data-testid={testId || `gated-inline-${feature}`}
        className="inline-flex items-center gap-1 px-2 py-1 rounded-[3px] bg-brand-orange/10 hover:bg-brand-orange/15 border border-brand-orange/30 text-brand-orange font-body text-[11px] font-semibold transition-colors"
        title={`Upgrade to ${planLabel(reqPlan)} to unlock`}
      >
        <Lock className="w-3 h-3" /> {planLabel(reqPlan)}
      </Link>
    );
  }

  if (licence && laScope !== licence) {
    return (
      <div className="rounded-[6px] border border-dashed border-brand-orange/40 bg-brand-orange/[0.04] p-5 flex items-start gap-4" data-testid={testId || `gated-card-${feature}`}>
        <div className="w-9 h-9 rounded-[4px] bg-brand-orange/15 flex items-center justify-center shrink-0">
          <Lock className="w-4 h-4 text-brand-orange" strokeWidth={2} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-display text-sm font-extrabold text-ink mb-1">{title || 'Freemium view'} — your licence covers {licenceName}</p>
          <p className="font-body text-xs text-ink/60 leading-relaxed mb-3">
            You're viewing <strong className="text-ink">{scopeName}</strong>. Full access is included for {licenceName}; other districts stay on the free tier until licensed.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={() => setLAScope(licence, { manual: true })} data-testid="gated-switch-licensed" className="bg-ink hover:bg-ink/90 text-white font-body text-xs h-8">
              Switch to {licenceName}
            </Button>
            <Button asChild size="sm" variant="outline" className="font-body text-xs h-8 border-brand-orange/40 text-brand-orange hover:bg-brand-orange/10">
              <Link to="/pricing" data-testid="gated-license-district">License this district</Link>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="rounded-[6px] border border-dashed border-brand-orange/40 bg-brand-orange/[0.04] p-5 flex items-start gap-4"
      data-testid={testId || `gated-card-${feature}`}
    >
      <div className="w-9 h-9 rounded-[4px] bg-brand-orange/15 flex items-center justify-center shrink-0">
        <Lock className="w-4 h-4 text-brand-orange" strokeWidth={2} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-display text-sm font-extrabold text-ink mb-1">
          {title || `${planLabel(reqPlan)} feature`}
        </p>
        <p className="font-body text-xs text-ink/60 leading-relaxed mb-3">
          Upgrade to <strong className="text-ink">{planLabel(reqPlan)}</strong> to unlock this. Free 14-day trial — no card required.
        </p>
        <Button asChild size="sm" className="bg-brand-orange hover:bg-brand-orange/90 text-white font-body text-xs h-8">
          <Link to="/pricing">Upgrade to {planLabel(reqPlan)}</Link>
        </Button>
      </div>
    </div>
  );
}
